import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";

interface MapTransform {
  scale: number;
  translateX: number;
  translateY: number;
}

const MIN_SCALE = 1;
const MAX_SCALE = 3;
const WHEEL_ZOOM_STEP = 0.002;

// カーソル/ピンチ位置基準ズーム: origin-center の CSS transform に対して
// 指定点を固定するための translate 補正を適用する。
// 導出: scale(S) translate(TX%, TY%) で screenX = center + S * (local + TX) から、
// 同一点を固定する条件 → adjust = (mx - 0.5) * (oldScale / newScale - 1) * 100
//
// 例: mx=0.5（中央）→ adjust=0（中央ズームは補正不要）
//     mx=1.0（右端）→ adjust=負（ズームイン時、左方向にシフトして右端を固定）
//     mx=0.0（左端）→ adjust=正（ズームイン時、右方向にシフトして左端を固定）
function applyZoomAtPoint(
  prev: MapTransform,
  newScale: number,
  mx: number,
  my: number,
  clamp: (tx: number, ty: number, scale: number) => { x: number; y: number }
): MapTransform {
  const adjustX = (mx - 0.5) * (prev.scale / newScale - 1) * 100;
  const adjustY = (my - 0.5) * (prev.scale / newScale - 1) * 100;
  const clamped = clamp(
    prev.translateX + adjustX,
    prev.translateY + adjustY,
    newScale
  );
  return { scale: newScale, translateX: clamped.x, translateY: clamped.y };
}

export function useMapTransform() {
  const [transform, setTransform] = useState<MapTransform>({
    scale: 1,
    translateX: 0,
    translateY: 0,
  });

  // ホイールイベントをネイティブリスナーとして登録するためのコンテナ要素
  // React 17+ の合成 onWheel は passive listener として登録され preventDefault が効かないため、
  // addEventListener({ passive: false }) で直接登録する必要がある。
  //
  // callback ref + useState パターンを採用する理由:
  //   - コンテナ要素は親コンポーネントの loading 完了後に初めてマウントされる
  //     （SeatMap は layout/seats/reservations のいずれかが loading 中は Skeleton を返す）
  //   - 通常の useRef + useEffect([deps]) だと、マウント完了後の要素アタッチをトリガーに
  //     effect を再実行できず、ネイティブ wheel リスナーが永久に登録されない
  //   - callback ref を useState に接続することで、要素のアタッチ/デタッチで
  //     useState が更新され、effect の依存配列によって自動的に登録/解除が走る
  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null);
  const containerRef = useCallback((node: HTMLDivElement | null) => {
    setContainerEl(node);
  }, []);

  // パン用のドラッグ状態
  const isPanning = useRef(false);
  const panStart = useRef({ x: 0, y: 0 });
  const panTranslateStart = useRef({ x: 0, y: 0 });

  // ピンチ用の状態
  const pinchStartDistance = useRef(0);
  const pinchStartScale = useRef(1);

  // 移動量の境界を制限
  const clampTranslate = useCallback(
    (tx: number, ty: number, scale: number) => {
      // ズームしていない場合は移動不可
      if (scale <= 1) {
        return { x: 0, y: 0 };
      }
      // スケールに応じた最大移動量（コンテナサイズの割合）
      const maxOffset = ((scale - 1) / scale) * 50;
      return {
        x: Math.max(-maxOffset, Math.min(maxOffset, tx)),
        y: Math.max(-maxOffset, Math.min(maxOffset, ty)),
      };
    },
    []
  );

  // ホイールズーム: ネイティブリスナーとして { passive: false } で登録し preventDefault を効かせる
  useEffect(() => {
    if (!containerEl) {
      return;
    }
    const handler = (e: WheelEvent) => {
      // NaN / Infinity ガード:
      // 通常のブラウザからは発生しないが、拡張機能や自動化ツールから
      // 異常値（NaN / Infinity）の deltaY が飛んでくると
      // Math.max(MIN, Math.min(MAX, prev.scale + NaN)) === NaN となり
      // CSS transform が NaN 汚染されて座席マップが描画崩壊する
      // （ローカル DoS）。防御として早期 return する。
      if (!Number.isFinite(e.deltaY)) {
        return;
      }
      e.preventDefault();
      const rect = containerEl.getBoundingClientRect();
      const mx = rect.width > 0 ? (e.clientX - rect.left) / rect.width : 0.5;
      const my = rect.height > 0 ? (e.clientY - rect.top) / rect.height : 0.5;
      setTransform((prev) => {
        const delta = -e.deltaY * WHEEL_ZOOM_STEP;
        const newScale = Math.max(
          MIN_SCALE,
          Math.min(MAX_SCALE, prev.scale + delta)
        );
        return applyZoomAtPoint(prev, newScale, mx, my, clampTranslate);
      });
    };
    containerEl.addEventListener("wheel", handler, { passive: false });
    return () => {
      containerEl.removeEventListener("wheel", handler);
    };
    // clampTranslate は useCallback([]) で不変だが、Biome useExhaustiveDependencies の
    // 要求に従い依存配列に含める。再登録トリガは containerEl の変化に限定される。
  }, [containerEl, clampTranslate]);

  // ダブルクリック/ダブルタップでリセット
  const handleDoubleClick = useCallback(() => {
    setTransform({ scale: 1, translateX: 0, translateY: 0 });
  }, []);

  // マウスドラッグによるパン
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (transform.scale <= 1) {
        return;
      }
      // 座席ボタン上のクリックはパンしない
      if ((e.target as HTMLElement).closest("button")) {
        return;
      }
      e.preventDefault();
      isPanning.current = true;
      panStart.current = { x: e.clientX, y: e.clientY };
      panTranslateStart.current = {
        x: transform.translateX,
        y: transform.translateY,
      };
    },
    [transform.scale, transform.translateX, transform.translateY]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isPanning.current) {
        return;
      }
      const container = (
        e.currentTarget as HTMLElement
      ).getBoundingClientRect();
      const dx = ((e.clientX - panStart.current.x) / container.width) * 100;
      const dy = ((e.clientY - panStart.current.y) / container.height) * 100;
      const clamped = clampTranslate(
        panTranslateStart.current.x + dx,
        panTranslateStart.current.y + dy,
        transform.scale
      );
      setTransform((prev) => ({
        ...prev,
        translateX: clamped.x,
        translateY: clamped.y,
      }));
    },
    [transform.scale, clampTranslate]
  );

  const handleMouseUp = useCallback(() => {
    isPanning.current = false;
  }, []);

  // タッチイベント（ピンチ + パン）
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length === 2) {
        // ピンチ開始
        const touch1 = e.touches[0];
        const touch2 = e.touches[1];
        if (touch1 && touch2) {
          const dx = touch1.clientX - touch2.clientX;
          const dy = touch1.clientY - touch2.clientY;
          pinchStartDistance.current = Math.hypot(dx, dy);
          pinchStartScale.current = transform.scale;
        }
      } else if (e.touches.length === 1 && transform.scale > 1) {
        // パン開始（ズーム中のみ）
        const touch = e.touches[0];
        if (touch) {
          isPanning.current = true;
          panStart.current = { x: touch.clientX, y: touch.clientY };
          panTranslateStart.current = {
            x: transform.translateX,
            y: transform.translateY,
          };
        }
      }
    },
    [transform.scale, transform.translateX, transform.translateY]
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length === 2) {
        // ピンチ中
        e.preventDefault();
        const touch1 = e.touches[0];
        const touch2 = e.touches[1];
        if (touch1 && touch2) {
          const dx = touch1.clientX - touch2.clientX;
          const dy = touch1.clientY - touch2.clientY;
          const distance = Math.hypot(dx, dy);
          if (pinchStartDistance.current === 0) {
            return;
          }
          const ratio = distance / pinchStartDistance.current;
          const newScale = Math.max(
            MIN_SCALE,
            Math.min(MAX_SCALE, pinchStartScale.current * ratio)
          );
          const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
          const midX = (touch1.clientX + touch2.clientX) / 2;
          const midY = (touch1.clientY + touch2.clientY) / 2;
          const mx = rect.width > 0 ? (midX - rect.left) / rect.width : 0.5;
          const my = rect.height > 0 ? (midY - rect.top) / rect.height : 0.5;
          setTransform((prev) =>
            applyZoomAtPoint(prev, newScale, mx, my, clampTranslate)
          );
        }
      } else if (e.touches.length === 1 && isPanning.current) {
        // パン中
        const touch = e.touches[0];
        if (touch) {
          const container = (
            e.currentTarget as HTMLElement
          ).getBoundingClientRect();
          const dx =
            ((touch.clientX - panStart.current.x) / container.width) * 100;
          const dy =
            ((touch.clientY - panStart.current.y) / container.height) * 100;
          const clamped = clampTranslate(
            panTranslateStart.current.x + dx,
            panTranslateStart.current.y + dy,
            transform.scale
          );
          setTransform((prev) => ({
            ...prev,
            translateX: clamped.x,
            translateY: clamped.y,
          }));
        }
      }
    },
    [transform.scale, clampTranslate]
  );

  const handleTouchEnd = useCallback(() => {
    isPanning.current = false;
    pinchStartDistance.current = 0;
  }, []);

  const isZoomed = transform.scale > 1;

  const resetZoom = useCallback(() => {
    setTransform({ scale: 1, translateX: 0, translateY: 0 });
  }, []);

  return {
    // ref prop に渡す callback ref（要素のアタッチ/デタッチで内部 state を更新）
    containerRef,
    // getBoundingClientRect 等で直接参照したい消費側向けの生要素
    containerEl,
    transform,
    isZoomed,
    resetZoom,
    handlers: {
      onDoubleClick: handleDoubleClick,
      onMouseDown: handleMouseDown,
      onMouseMove: handleMouseMove,
      onMouseUp: handleMouseUp,
      onMouseLeave: handleMouseUp,
      onTouchStart: handleTouchStart,
      onTouchMove: handleTouchMove,
      onTouchEnd: handleTouchEnd,
    },
  };
}
